/**
 * How much ground each item covers, in voxels.
 *
 * This lives in the domain rather than beside the voxel art because the worker has to enforce the
 * same boundary the client draws, and the worker cannot import client rendering code. The numbers
 * are the true axis-aligned bounds of an item's voxels — deliberately *not* its authored
 * `dimensions`, which is a decorative field and wrong in places (the stone bird bath declares
 * 7 x 7 and spans nearly 7.7).
 *
 * Where an item has variants that change its silhouette, the entry is the largest envelope across
 * that item's whole variant space, so the client's clamp and the server's check always agree
 * whichever variant is in play.
 *
 * Generated from the art and kept honest by a drift test rather than a build step — see
 * `apps/activity/src/world/assets/catalog/bridge.test.ts`.
 */

export type Footprint = {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /** Tallest point, measured from the art's own origin. */
  height: number;
  /**
   * Lowest point, also from the art's origin, and never above it.
   *
   * The renderer stands an item by putting its *lowest* voxel on the surface, so what an item
   * actually occupies is `height - baseY`, not `height`. Stacking is where the difference shows:
   * measuring a table's top as `height` alone put whatever stood on it in the wrong place by
   * exactly this much. Absent means zero.
   */
  baseY?: number;
  /**
   * Height above `baseY` of the item's broadest upper surface — where a thing put on it rests.
   *
   * Measured rather than assumed, because "the top" and "the surface you can stand something on"
   * are different heights for most furniture.
   */
  surfaceY?: number;
};

/** How tall an item actually stands, in art units — the span the renderer draws. */
export function footprintHeight(footprint: Footprint): number {
  return footprint.height - (footprint.baseY ?? 0);
}

/**
 * How high above its own base an item offers something to stand on, in art units.
 *
 * Not the same as its height, and that difference is the whole point. A table's bounding box is
 * topped by whatever ornament the art sits on it — a bowl, a book — so stacking against the box
 * left a plant hovering over the table by the height of the bowl. This is the *broad* surface: the
 * highest level where the item is still substantially solid, which is the table top rather than the
 * thing on it.
 *
 * Falls back to the full height, so an item measured before this existed still stacks, just
 * generously.
 */
export function footprintSurface(footprint: Footprint): number {
  return footprint.surfaceY ?? footprintHeight(footprint);
}

/**
 * Used for any item with no measured entry. Generous on purpose: an unknown item should be
 * awkward to place rather than able to escape the fence.
 */
export const DEFAULT_FOOTPRINT: Footprint = { minX: -3, maxX: 3, minZ: -3, maxZ: 3, height: 4 };

/** Measured bounds by domain catalog item id. Populated from the art. */
export const ITEM_FOOTPRINTS: Record<string, Footprint> = {
  "furniture-hearth-1": { minX: -4.5, maxX: 3.5, minZ: -2.5, maxZ: 1.5, height: 8, baseY: -0.5, surfaceY: 8 },
  "furniture-hearth-2": { minX: -3, maxX: 2, minZ: -3, maxZ: 3.35, height: 5, baseY: -0.5, surfaceY: 4 },
  "furniture-hearth-3": { minX: -3.75, maxX: 2.75, minZ: -2, maxZ: 1.2, height: 11, baseY: -0.5, surfaceY: 11 },
  "furniture-hearth-4": { minX: -4.5, maxX: 4.5, minZ: -3.5, maxZ: 3.5, height: 0.5, surfaceY: 0.1 },
  "furniture-hearth-5": { minX: -1.5, maxX: 1.45, minZ: -1.5, maxZ: 1.45, height: 9.5, baseY: -0.5, surfaceY: 9.9 },
  "furniture-hearth-6": { minX: -3.5, maxX: 3.5, minZ: -3.5, maxZ: 3.5, height: 9, baseY: -0.5, surfaceY: 4 },
  "furniture-hearth-7": { minX: -3.25, maxX: 3.25, minZ: -3.25, maxZ: 3.25, height: 1.5, baseY: -0.25, surfaceY: 1.5 },
  "furniture-hearth-8": { minX: -3.5, maxX: 2.5, minZ: -1.5, maxZ: 0.5, height: 11.5, surfaceY: 9.25 },
  "furniture-cottage-1": { minX: -3.5, maxX: 2.5, minZ: -4.5, maxZ: 3.5, height: 4, baseY: -0.5, surfaceY: 2.5 },
  "furniture-cottage-2": { minX: -4, maxX: 3, minZ: -3, maxZ: 6.35, height: 12, baseY: -0.5, surfaceY: 12 },
  "furniture-cottage-3": { minX: -2.5, maxX: 1.5, minZ: -2.5, maxZ: 3.35, height: 5, baseY: -0.5, surfaceY: 4 },
  "furniture-cottage-4": { minX: -5, maxX: 4, minZ: -3, maxZ: 5.35, height: 7.5, baseY: -0.5, surfaceY: 6 },
  "furniture-cottage-5": { minX: -3, maxX: 2, minZ: -1.5, maxZ: 0.7, height: 6.5, baseY: -0.5, surfaceY: 6.8 },
  "furniture-cottage-6": { minX: -2.5, maxX: 1.75, minZ: -2, maxZ: 1, height: 9, baseY: -0.5, surfaceY: 8.25 },
  "furniture-cottage-7": { minX: -3.5, maxX: 2.7, minZ: -2.6, maxZ: 2.6, height: 5, baseY: -0.5, surfaceY: 5 },
  "furniture-cottage-8": { minX: -3, maxX: 2, minZ: -2.5, maxZ: 1.5, height: 7.5, baseY: -0.5, surfaceY: 4.5 },
  "furniture-kitchen-1": { minX: -4, maxX: 3.25, minZ: -2.5, maxZ: 2.25, height: 12.5, baseY: -0.5, surfaceY: 12 },
  "furniture-kitchen-2": { minX: -4, maxX: 3, minZ: -2.5, maxZ: 1.5, height: 11, baseY: -0.5, surfaceY: 9.75 },
  "furniture-kitchen-3": { minX: -2, maxX: 1.5, minZ: -2, maxZ: 1.5, height: 4, baseY: -0.5, surfaceY: 4 },
  "furniture-kitchen-4": { minX: -5.25, maxX: 5.25, minZ: -1.5, maxZ: 1.75, height: 6, baseY: -0.5, surfaceY: 6.25 },
  "furniture-kitchen-5": { minX: -4.25, maxX: 3.15, minZ: -2.5, maxZ: 1.5, height: 11, baseY: -0.5, surfaceY: 11 },
  "furniture-kitchen-6": { minX: -3, maxX: 2, minZ: -3.75, maxZ: 1.5, height: 12.5, baseY: -0.5, surfaceY: 4.5 },
  "furniture-kitchen-7": { minX: -4.75, maxX: 4.25, minZ: -1.25, maxZ: 0, height: 9.5, surfaceY: 6.25 },
  "furniture-kitchen-8": { minX: -5.5, maxX: 4.5, minZ: -1.5, maxZ: 0.7, height: 2.5, baseY: -0.5, surfaceY: 2.5 },
  "furniture-workshop-1": { minX: -4, maxX: 5.75, minZ: -2.5, maxZ: 2.75, height: 11.5, baseY: -0.5, surfaceY: 6.2 },
  "furniture-workshop-2": { minX: -4.5, maxX: 3.75, minZ: -3.25, maxZ: 2, height: 12, baseY: -0.5, surfaceY: 3 },
  "furniture-workshop-3": { minX: -1.7, maxX: 1.3, minZ: -1.7, maxZ: 1.3, height: 5.5, baseY: -0.5, surfaceY: 5.8 },
  "furniture-workshop-4": { minX: -3.5, maxX: 2.9, minZ: -2, maxZ: 1.9, height: 11.5, baseY: -0.4, surfaceY: 9.4 },
  "furniture-workshop-5": { minX: -3.5, maxX: 2.7, minZ: -2, maxZ: 1.8, height: 11, baseY: -0.5, surfaceY: 11 },
  "furniture-workshop-6": { minX: -5, maxX: 4, minZ: -3, maxZ: 2, height: 10.5, baseY: -0.5, surfaceY: 4 },
  "furniture-workshop-7": { minX: -2.45, maxX: 2.45, minZ: -2.45, maxZ: 2.45, height: 6.5, baseY: -0.45, surfaceY: 4.9 },
  "furniture-workshop-8": { minX: -3.75, maxX: 1.5, minZ: -2, maxZ: 3.25, height: 13.5, baseY: -0.5, surfaceY: 13.75 },
  "furniture-gardenRoom-1": { minX: -3, maxX: 2, minZ: -2.5, maxZ: 1.5, height: 6, baseY: -0.5, surfaceY: 6.2 },
  "furniture-gardenRoom-2": { minX: -4.5, maxX: 3.5, minZ: -2.7, maxZ: 2.3, height: 11.5, baseY: -0.5, surfaceY: 4 },
  "furniture-gardenRoom-3": { minX: -5.25, maxX: 3.3, minZ: -2.3, maxZ: 3.35, height: 10.5, baseY: -0.5, surfaceY: 10.75 },
  "furniture-gardenRoom-4": { minX: -4.75, maxX: 3.25, minZ: -2.75, maxZ: 0.5, height: 18, baseY: -0.5, surfaceY: 18.05 },
  "furniture-gardenRoom-5": { minX: -3.5, maxX: 3.5, minZ: -3.5, maxZ: 3.5, height: 5, baseY: -0.5, surfaceY: 5.25 },
  "furniture-gardenRoom-6": { minX: -2.75, maxX: 2.75, minZ: -2.75, maxZ: 2.75, height: 11, baseY: -0.5, surfaceY: 11.25 },
  "furniture-gardenRoom-7": { minX: -3.5, maxX: 3.4, minZ: -2.25, maxZ: 0.5, height: 12.5, baseY: -0.4, surfaceY: 12.15 },
  "furniture-gardenRoom-8": { minX: -3.5, maxX: 2.7, minZ: -2, maxZ: 1, height: 3, baseY: -0.5, surfaceY: 3.25 },
  "furniture-gathering-1": { minX: -6.5, maxX: 5.5, minZ: -3, maxZ: 2, height: 4.5, baseY: -0.5, surfaceY: 4.5 },
  "furniture-gathering-2": { minX: -1.7, maxX: 1.5, minZ: -1.7, maxZ: 1.5, height: 8.5, baseY: -0.5, surfaceY: 8.6 },
  "furniture-gathering-3": { minX: -3.5, maxX: 2.5, minZ: -3.5, maxZ: 2.9, height: 5.5, baseY: -0.5, surfaceY: 5.6 },
  "furniture-gathering-4": { minX: -4.25, maxX: 2.75, minZ: -2.3, maxZ: 1.7, height: 13.5, baseY: -0.5, surfaceY: 5 },
  "furniture-gathering-5": { minX: -4.25, maxX: 2.5, minZ: -2, maxZ: 1.2, height: 9, baseY: -0.5, surfaceY: 9.25 },
  "furniture-gathering-6": { minX: -2.5, maxX: 2.3, minZ: -2, maxZ: 1.6, height: 9.5, baseY: -0.5, surfaceY: 4.5 },
  "furniture-gathering-7": { minX: -4.5, maxX: 4.5, minZ: -4.5, maxZ: 4.5, height: 1, baseY: -0.65, surfaceY: 1.5 },
  "furniture-gathering-8": { minX: -3.5, maxX: 3.5, minZ: -2.7, maxZ: 2.3, height: 4, baseY: -0.5, surfaceY: 4.15 },
  "exterior-1": { minX: -2.5, maxX: 1.5, minZ: -2.5, maxZ: 1.75, height: 9, baseY: -0.5, surfaceY: 9 },
  "exterior-2": { minX: -4.5, maxX: 3.7, minZ: -2, maxZ: 1, height: 5.5, baseY: -0.5, surfaceY: 5.1 },
  "exterior-3": { minX: -4.25, maxX: 3.5, minZ: -3.5, maxZ: 3.5, height: 6, baseY: -0.5, surfaceY: 5 },
  "exterior-4": { minX: -2.75, maxX: 1.75, minZ: -2, maxZ: 1, height: 2.5, baseY: -0.5, surfaceY: 2.75 },
  "exterior-5": { minX: -7.5, maxX: 6.5, minZ: -1, maxZ: 0.25, height: 7, baseY: -0.5, surfaceY: 6.75 },
  "exterior-6": { minX: -1.75, maxX: 0.5, minZ: -1.5, maxZ: 3.75, height: 6, surfaceY: 5.5 },
  "exterior-7": { minX: -3.25, maxX: 2.75, minZ: -2.25, maxZ: 2.25, height: 1, baseY: -0.25, surfaceY: 1 },
  "exterior-8": { minX: -4.8, maxX: 3.8, minZ: -1.7, maxZ: 1.5, height: 12, baseY: -0.5, surfaceY: 12 },
  "exterior-9": { minX: -2.25, maxX: 2, minZ: -2.75, maxZ: -0.5, height: 6.5, baseY: -0.5, surfaceY: 4.75 },
  "exterior-10": { minX: -4.75, maxX: 5.5, minZ: -2.75, maxZ: 2.75, height: 5, baseY: -0.45, surfaceY: 5 },
  "exterior-11": { minX: -5.5, maxX: 5.5, minZ: -2.5, maxZ: 1.7, height: 3.5, baseY: -0.30000000000000004, surfaceY: 3.3499999999999996 },
  "exterior-12": { minX: -3.5, maxX: 2.5, minZ: -2.5, maxZ: 1.5, height: 5, baseY: -0.5, surfaceY: 5 },
  "exterior-13": { minX: -2.5, maxX: 2.475, minZ: -2.5, maxZ: 2.475, height: 5, baseY: -0.475, surfaceY: 4.944999999999999 },
  "exterior-14": { minX: -2.4, maxX: 2.4, minZ: -2.4, maxZ: 2.4, height: 9, baseY: -0.3, surfaceY: 8.5 },
  "exterior-15": { minX: -5.5, maxX: 5.5, minZ: -4.5, maxZ: 4.5, height: 5, surfaceY: 0.1 },
  "exterior-16": { minX: -2.5, maxX: 2.5, minZ: -2.5, maxZ: 2.5, height: 7.5, baseY: -0.5, surfaceY: 7.5 },
  "exterior-17": { minX: -1.5, maxX: 0.5, minZ: -1.25, maxZ: 0, height: 12, surfaceY: 11.5 },
  "exterior-18": { minX: -4, maxX: 4, minZ: -4, maxZ: 4, height: 4.5, baseY: -0.5, surfaceY: 4.5 },
  "exterior-19": { minX: -3.5, maxX: 3.5, minZ: -3, maxZ: 2, height: 12, baseY: -0.5, surfaceY: 12.3 },
  "exterior-20": { minX: -3.5, maxX: 3.5, minZ: -3.5, maxZ: 3.5, height: 4, baseY: -0.5, surfaceY: 4 },
  "exterior-21": { minX: -5, maxX: 5.5, minZ: -2, maxZ: 1, height: 9, baseY: -0.5, surfaceY: 8.5 },
  "exterior-22": { minX: -2, maxX: 1, minZ: -2, maxZ: 1, height: 6, baseY: -0.5, surfaceY: 5.85 },
  "exterior-23": { minX: -2.5, maxX: 4.5, minZ: -2.2, maxZ: 2.2, height: 3.5, baseY: -0.4, surfaceY: 2.4 },
  "exterior-24": { minX: -3.75, maxX: 2.5, minZ: -1.1, maxZ: 1.25, height: 8, surfaceY: 6.75 },
  "exterior-25": { minX: -3.5, maxX: 2.5, minZ: -1, maxZ: 0.4, height: 15, baseY: -0.5, surfaceY: 13 },
  "exterior-26": { minX: -4.75, maxX: 6.75, minZ: -2.75, maxZ: -0.25, height: 4, baseY: -0.25, surfaceY: 2.5 },
  "exterior-27": { minX: -3.75, maxX: 1.75, minZ: -1.7, maxZ: 1.7, height: 9.5, baseY: -0.2, surfaceY: 8.95 },
  "exterior-28": { minX: -2.75, maxX: 4.3, minZ: -2.5, maxZ: 1.5, height: 4.5, baseY: -0.5, surfaceY: 3.2 },
  "exterior-29": { minX: -3.75, maxX: 2.75, minZ: -2.75, maxZ: 1.75, height: 5.5, baseY: -0.25, surfaceY: 3.5 },
  "exterior-30": { minX: -5, maxX: 5, minZ: -1.5, maxZ: 0.5, height: 13, baseY: -0.5, surfaceY: 13 },
  "plant-1": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 6.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-2": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 7.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-3": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 7, baseY: -0.25, surfaceY: 3.5 },
  "plant-4": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 8, baseY: -0.25, surfaceY: 3.5 },
  "plant-5": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 7, baseY: -0.25, surfaceY: 5.4 },
  "plant-6": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 6.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-7": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 6, baseY: -0.25, surfaceY: 3.5 },
  "plant-8": { minX: -3.25, maxX: 2.25, minZ: -3.25, maxZ: 2.25, height: 4.5, baseY: -0.25, surfaceY: 4.5 },
  "plant-9": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 9, baseY: -0.25, surfaceY: 3.5 },
  "plant-10": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 8.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-11": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 7.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-12": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 8, baseY: -0.25, surfaceY: 3.5 },
  "plant-13": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 7.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-14": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 5.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-15": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 6.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-16": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 6, baseY: -0.25, surfaceY: 3.5 },
  "plant-17": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 5.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-18": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 5.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-19": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 5.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-20": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 6.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-21": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 9, baseY: -0.25, surfaceY: 3.5 },
  "plant-22": { minX: -2.75, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 5.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-23": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 11.5, baseY: -0.25, surfaceY: 3.5 },
  "plant-24": { minX: -2.55, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 4.5, baseY: -0.25, surfaceY: 3.5 },
  "pet-cat": { minX: -1.25, maxX: 0.75, minZ: -2.75, maxZ: 2.25, height: 5, baseY: -0.25, surfaceY: 4 },
  "pet-dog": { minX: -2, maxX: 1, minZ: -3, maxZ: 3, height: 4.5, baseY: -0.25, surfaceY: 4.5 },
  "pet-rabbit": { minX: -1.75, maxX: 0.75, minZ: -2.25, maxZ: 1.5, height: 6.5, baseY: -0.25, surfaceY: 4 },
  "pet-chicken": { minX: -1.75, maxX: 1.25, minZ: -2.75, maxZ: 2.5, height: 5.5, baseY: -0.25, surfaceY: 3.5 },
  "pet-turtle": { minX: -2.25, maxX: 1.25, minZ: -2.25, maxZ: 2.75, height: 3, baseY: -0.25, surfaceY: 2.75 },
  "pet-sheep": { minX: -2.5, maxX: 2, minZ: -3.25, maxZ: 4.25, height: 4.5, baseY: -0.25, surfaceY: 4 },
  "rare-1": { minX: -2.5, maxX: 2.1, minZ: -2.5, maxZ: 1.5, height: 12.5, baseY: -0.5, surfaceY: 12.5 },
  "rare-2": { minX: -5.5, maxX: 5.5, minZ: -5.5, maxZ: 5.5, height: 8.5, baseY: -0.5, surfaceY: 6 },
  "rare-3": { minX: -4.45, maxX: 5.75, minZ: -3.45, maxZ: 3.45, height: 6, baseY: -1.45, surfaceY: 3.7 },
  "rare-4": { minX: -1.75, maxX: 1.75, minZ: -0.25, maxZ: 0.25, height: 13.5, baseY: -0.2, surfaceY: 13.45 },
  "rare-5": { minX: -2.25, maxX: 2.25, minZ: -2.25, maxZ: 2.25, height: 8.5, baseY: -0.5, surfaceY: 8.75 },
  "rare-6": { minX: -4.5, maxX: 3.5, minZ: -1.5, maxZ: 0.5, height: 11, baseY: -0.5, surfaceY: 11 },
  "rare-7": { minX: -2.5, maxX: 1.5, minZ: -2.5, maxZ: 1.5, height: 10, baseY: -0.5, surfaceY: 10 },
  "rare-8": { minX: -3.7, maxX: 3.3, minZ: -3.3, maxZ: 2.5, height: 12.5, baseY: -0.5, surfaceY: 11.5 },
  "rare-9": { minX: -5, maxX: 4.3, minZ: -3, maxZ: 2.3, height: 11, baseY: -0.5, surfaceY: 11 },
  "rare-10": { minX: -5, maxX: 4, minZ: -2.45, maxZ: 2.45, height: 13, baseY: -0.475, surfaceY: 12.924999999999999 },
  "rare-11": { minX: -2.5, maxX: 1.5, minZ: -2.5, maxZ: 1.5, height: 8, baseY: -0.5, surfaceY: 7.25 },
  "rare-12": { minX: -3.5, maxX: 5.5, minZ: -2.75, maxZ: 2.75, height: 6, baseY: -0.45, surfaceY: 4.4 },
};

export function getFootprint(itemId: string): Footprint {
  return ITEM_FOOTPRINTS[itemId] ?? DEFAULT_FOOTPRINT;
}

/** Half-extents and centre offset, which is the form the boundary maths actually wants. */
export function footprintExtent(footprint: Footprint): {
  offsetX: number;
  offsetZ: number;
  halfX: number;
  halfZ: number;
} {
  return {
    offsetX: (footprint.minX + footprint.maxX) / 2,
    offsetZ: (footprint.minZ + footprint.maxZ) / 2,
    halfX: (footprint.maxX - footprint.minX) / 2,
    halfZ: (footprint.maxZ - footprint.minZ) / 2
  };
}
